import FeedHeader, { FeedHeaderProps, FeedViewAllFooter } from './feed-header';
import { Content } from '../../../models/content/types';
import './content-layouts.scss';
import {
  FeedCardWithData,
  MobileScrollCard,
} from '../../content/solar-feed-card/solar-feed-card';
import {
  FeedCardGrid,
  FeedCardGridItem,
  FeedCardHorizontalScroller,
} from '@socialchorus/shared-ui-components';
import useScreenSize from '../../../common/use-screen-size';
import { MobileHorizontalFeedCards } from './horizontal-scroll-row';

/** Hero item counts per mobile-layouts tier: the display-slicing and dedup cap
 * for hero. home-feeds.tsx's fetch per_page is a separate constant
 * (MAX_DISPLAY_CONTENTS_MAP) sized to cover this table's widest tier. */
const HERO_TIER_ITEMS = {
  tablet: { featured: 2, sidecards: 4 },
  default: { featured: 1, sidecards: 3 },
} as const;

export const heroTierItemCount = (isTabletHero: boolean) => {
  const { featured, sidecards } =
    HERO_TIER_ITEMS[isTabletHero ? 'tablet' : 'default'];
  return featured + sidecards;
};

type ContentLayoutHeroProps = FeedHeaderProps & {
  contents: Content[];
  description?: string;
  feedUrl: string;
  viewAllText: string;
  isTopic: boolean;
  /** "detailed" hero (FE-32783): show a body excerpt on the hero card. */
  detailed?: boolean;
  /** Which HERO_TIER_ITEMS entry to slice by — the caller owns the tier
   * decision so it stays coupled to whatever cap it fetched. */
  tier?: keyof typeof HERO_TIER_ITEMS;
  /** The caller decides whether mobile-web treatment applies here — it also
   * owns the container markup those layouts depend on, which this component
   * has no visibility into. */
  mobileWebLayouts: boolean;
  mobileWebParity: boolean;
};

const ContentLayoutHero = ({
  id,
  contents,
  analyticsData,
  title,
  description,
  feedUrl,
  viewAllText,
  viewAllContext,
  isTopic = false,
  detailed = false,
  tier = 'default',
  mobileWebLayouts,
  mobileWebParity,
}: ContentLayoutHeroProps) => {
  const { isMobile } = useScreenSize();
  const isMobileModule = isMobile && !!(mobileWebLayouts || mobileWebParity);

  // Gated on the flag, not just width: at 600-799px with the flag off, the
  // desktop branch below must keep slicing at 1 + 3, not 2 + 4.
  const isMobileLayout = isMobile && !!mobileWebLayouts;
  const { featured, sidecards } = HERO_TIER_ITEMS[tier];
  const featuredContents = contents.slice(0, featured);
  const sidecardContents = contents.slice(featured, featured + sidecards);
  const hasSidecards = sidecardContents.length > 0;
  const heroContent = featuredContents[0];

  return (
    <>
      <FeedHeader
        id={id}
        title={title}
        description={description}
        viewAllLink={feedUrl}
        viewAllText={viewAllText}
        viewAllContext={viewAllContext}
        analyticsData={analyticsData}
        isTopic={isTopic}
        isMobileModule={isMobileModule}
      />

      {isMobileLayout ? (
        <>
          <div className="content-layout__mobile-gutter">
            {/* a lone half-width hero card would read as broken. */}
            <FeedCardGrid columns={featuredContents.length > 1 ? 2 : 1}>
              {featuredContents.map((content) => (
                <FeedCardGridItem key={content.id}>
                  <FeedCardWithData
                    content={content}
                    analyticsData={analyticsData}
                    layout="vertical-minimal"
                    compact
                    fullHeight
                    titleHeadingLevel={2}
                  />
                </FeedCardGridItem>
              ))}
            </FeedCardGrid>
          </div>
          {hasSidecards && (
            <div className="content-layout__mobile-scroller">
              <FeedCardHorizontalScroller
                itemWidth="minimal"
                items={sidecardContents.map((content) => (
                  <MobileScrollCard
                    key={content.id}
                    content={content}
                    analyticsData={analyticsData}
                    titleHeadingLevel={2}
                  />
                ))}
              />
            </div>
          )}
        </>
      ) : isMobile && mobileWebParity ? (
        <MobileHorizontalFeedCards
          contents={contents}
          analyticsData={analyticsData}
          title={title}
        />
      ) : (
        <FeedCardGrid columns={isMobile ? 1 : 2}>
          <FeedCardGridItem rowSpan={3}>
            <FeedCardWithData
              content={heroContent}
              analyticsData={analyticsData}
              compact
              fullHeight
              layout="vertical"
              titleHeadingLevel={2}
              // Only reduce descriptionLineClamp when body_excerpt is actually
              // present — otherwise the card degrades to 2 lines with no
              // preview text.
              descriptionLineClamp={
                detailed && !!heroContent.body_excerpt ? 2 : 5
              }
              inlineVideo={true}
              showBodyPreview={detailed}
            />
          </FeedCardGridItem>
          {sidecardContents.map((content) => (
            <FeedCardGridItem key={content.id}>
              <FeedCardWithData
                content={content}
                analyticsData={analyticsData}
                compact
                fullHeight
                layout="horizontal"
                titleHeadingLevel={2}
              />
            </FeedCardGridItem>
          ))}
        </FeedCardGrid>
      )}
      {isMobileModule && feedUrl && (
        <FeedViewAllFooter
          viewAllLink={feedUrl}
          viewAllText={viewAllText}
          viewAllContext={viewAllContext}
          isTopic={isTopic}
          analyticsData={analyticsData}
        />
      )}
    </>
  );
};

export default ContentLayoutHero;
